import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useApp } from "../state/app";
import { nf, useNorms } from "../hooks/useNorms";
import CoverageBars, { type CoverageSeg } from "../components/CoverageBars";
import EventCard, { describe, kindColor, type FeedItem } from "../components/EventCard";
import { FileItem, FilesSheet, useOpenFile } from "../components/FilesList";
import { fmtD, fmtDT, fmtT, isoToSec, secToIso } from "../utils/period";
import type { FileRow } from "../types";

interface HomeOut {
  coverage: Record<string, CoverageSeg[]>;
  units: { unit: number; data_from: string; data_to: string; run_min: number; p_max: number | null; energy_kwh: number;
           kvarh_out: number; kvarh_in: number;
           day: string; day_run_min: number; day_p_max: number | null; day_energy_kwh: number; day_minutes: number;
           day_kvarh_out: number; day_kvarh_in: number }[];
  events: FeedItem[];
  files: FileRow[];
  reports: { id: number; unit: number; from_ts: string; to_ts: string; filename: string; pages: number; bytes: number; created_at: string; url: string }[];
  station: { max_kw: number; energy_kwh: number; kvarh_out: number; kvarh_in: number; run_min: number } | null;
  alarms_last: string | null;
}

const hm = (min: number) => `${Math.floor(min / 60)} ч ${String(min % 60).padStart(2, "0")} мин`;
const n1 = (x: number | null | undefined, d = 1) => (x == null ? "—" : x.toFixed(d).replace(".", ","));
const n0 = (x: number | null | undefined) => (x == null ? "—" : String(Math.round(x)));
const dmy = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

// «Главная»: what data there is and until when, the last uploaded files right under it,
// station totals, the last events found in telemetry and reports. Every list item opens
// the right place: a file or an event → «Телеметрия» on that period, a report → the PDF.
export default function HomePanel({ hasUnits, hasPa, hasEvents }: {
  hasUnits: boolean; hasPa: boolean; hasEvents: boolean;
}) {
  const { t, lang } = useT();
  const norms = useNorms();
  const app = useApp();
  const [d, setD] = useState<HomeOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [card, setCard] = useState<FeedItem | null>(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const closeFiles = useCallback(() => setFilesOpen(false), []);
  useEffect(() => { api.get<HomeOut>("/api/home").then(setD).catch((e: ApiError) => setErr(e.message)); }, []);

  const unitSrc = (u: number): "g1" | "g2" => (u === 2 ? "g2" : "g1");
  const openEvent = (ev: FeedItem) => { tg()?.HapticFeedback?.impactOccurred("light"); setCard(ev); };
  const openFile = useOpenFile();
  const openUnitDay = (u: HomeOut["units"][number]) => {
    tg()?.HapticFeedback?.impactOccurred("light");
    app.openTelemetry(unitSrc(u.unit), { kind: "preset", preset: "lastday", from: `${u.day}T00:00:00+00:00`, to: secToIso(isoToSec(u.data_to) + 59) });
  };
  const openReport = (r: HomeOut["reports"][number]) => {
    const url = `${window.location.origin}${r.url}`;
    const w = tg();
    if (w?.openLink) w.openLink(url); else window.open(url, "_blank");
  };
  const link = (label: string, onClick: () => void) => (
    <span style={{ fontSize: 12, color: "var(--link)", cursor: "pointer" }} onClick={onClick}>{label} ›</span>
  );
  // reactive energy delivered / absorbed, both in the unit of the larger one
  const react = (o: number, i: number) => {
    const mega = Math.max(o, i) >= 1000;
    const f = (x: number) => (mega ? n1(x / 1000) : n0(x));
    return t("home.react", { out: f(o), inn: f(i), u: mega ? t("auto.mvarh") : t("auto.kvarh") });
  };

  if (err) return <div><h1>{t("home.title")}</h1><div className="alert error">{err}</div></div>;
  if (!d) return <div><h1>{t("home.title")}</h1><div className="empty-state"><span className="spinner" /></div></div>;

  const covRows = [
    ...(hasUnits ? [{ key: "g1", label: "G1", color: "#2a78d6", segs: d.coverage.g1 ?? [] }, { key: "g2", label: "G2", color: "#eb6834", segs: d.coverage.g2 ?? [] }] : []),
    ...(hasPa ? [{ key: "pa", label: t("telemetry.src_pa"), color: "#1baf7a", segs: d.coverage.pa ?? [] }] : []),
    ...(hasEvents ? [{ key: "alarms", label: t("home.alarms"), color: "#8593a0", segs: d.coverage.alarms ?? [] }] : []),
  ].filter(r => r.segs.length);
  const lastOf = (segs?: CoverageSeg[]) => (segs && segs.length ? segs[segs.length - 1].to : null);
  const lastLine = [
    ...(hasUnits ? [`G1 — ${lastOf(d.coverage.g1) ? fmtDT(lastOf(d.coverage.g1)!).slice(0, 16) : "—"}`, `G2 — ${lastOf(d.coverage.g2) ? fmtD(lastOf(d.coverage.g2)!) : "—"}`] : []),
    ...(hasPa ? [`${t("telemetry.src_pa").toLowerCase()} — ${lastOf(d.coverage.pa) ? fmtD(lastOf(d.coverage.pa)!) : "—"}`] : []),
    ...(hasEvents && d.alarms_last ? [`${t("home.alarms").toLowerCase()} — ${fmtD(d.alarms_last)}`] : []),
  ].join(" · ");

  return (
    <div>
      <h1>{t("home.title")}</h1>

      {d.station && (
        <div className="card" style={{ marginBottom: 10, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {[[t("home.peak"), n0(d.station.max_kw), "кВт"], [t("home.energy"), n1(d.station.energy_kwh / 1000), "МВт·ч"], [t("home.run"), String(Math.round(d.station.run_min / 60)), "ч"]].map(([l, v, u]) => (
            <div key={l} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "var(--hint)", textTransform: "uppercase", letterSpacing: ".04em" }}>{l}</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{v}<span style={{ fontSize: 12, fontWeight: 500, color: "var(--hint)", marginLeft: 3 }}>{u}</span></div>
            </div>
          ))}
          <div style={{ gridColumn: "1 / -1", fontSize: 12, color: "var(--hint)" }}>{react(d.station.kvarh_out ?? 0, d.station.kvarh_in ?? 0)}</div>
        </div>
      )}

      {covRows.length > 0 && (
        <div className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
            <b style={{ fontSize: 14 }}>{t("home.coverage")}</b>
            {hasUnits && link(t("home.open_telemetry"), () => app.openTelemetry(app.source === "pa" ? "g1" : app.source))}
          </div>
          <CoverageBars rows={covRows} focusKeys={["g1", "g2", "pa"]} />
          <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 6 }}>{t("home.last_data")}: {lastLine}</div>
        </div>
      )}

      {d.files.length > 0 && (
        <div className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
            <b style={{ fontSize: 14 }}>{t("files.title")}</b>
            {link(t("home.all"), () => setFilesOpen(true))}
          </div>
          {d.files.map((f, i) => <FileItem key={f.kind + f.id} f={f} divider={i > 0} onOpen={openFile} />)}
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 4 }}>{t("files.hint")}</div>
        </div>
      )}
      {filesOpen && <FilesSheet onClose={closeFiles} hasUnits={hasUnits} hasPa={hasPa} />}

      {hasUnits && d.units.length > 0 && (
        <div className="auto-tiles" style={{ gridTemplateColumns: d.units.length > 1 ? "1fr 1fr" : "1fr", marginBottom: 10 }}>
          {d.units.map(u => (
            <div key={u.unit} className="auto-tile" style={{ cursor: "pointer" }} onClick={() => openUnitDay(u)}>
              <div className="auto-tile-top"><span className="auto-tile-title">G{u.unit} · {dmy(u.day)}</span><span style={{ marginLeft: "auto", opacity: 0.5 }}>›</span></div>
              <div className="auto-tile-bigrow"><span className="auto-tile-big">{u.day_energy_kwh >= 1000 ? n1(u.day_energy_kwh / 1000, 2) : n0(u.day_energy_kwh)}</span>
                <span className="auto-tile-unit">{u.day_energy_kwh >= 1000 ? "МВт·ч" : "кВт·ч"}</span></div>
              <div style={{ fontSize: 11, color: "var(--hint)" }}>{t("home.unit_day", { run: hm(u.day_run_min), kw: n0(u.day_p_max) })}</div>
              <div style={{ fontSize: 11, color: "var(--hint)" }}>{react(u.day_kvarh_out ?? 0, u.day_kvarh_in ?? 0)}</div>
            </div>
          ))}
        </div>
      )}

      {hasUnits && (
        <div className="card" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <b style={{ fontSize: 14 }}>{t("home.events")}</b>
            {hasEvents && link(t("home.all"), () => app.setTab("events"))}
          </div>
          {d.events.length === 0 && <div style={{ fontSize: 13, color: "var(--hint)" }}>{t("home.no_events")}</div>}
          {d.events.map((ev, i) => (
            <div key={i} onClick={() => openEvent(ev)} style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 8, alignItems: "center", padding: "7px 0", borderTop: i ? "1px solid var(--border)" : undefined, cursor: "pointer", fontSize: 13 }}>
              <i style={{ width: 8, height: 8, borderRadius: "50%", background: kindColor(ev.kind) }} />
              <div>{describe(ev, t).title}<div style={{ fontSize: 11, color: "var(--hint)" }}>{describe(ev, t).sub}</div></div>
              <div style={{ fontSize: 11, color: "var(--hint)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtD(ev.ts).slice(0, 5)}<br />{fmtT(ev.ts)}</div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 6 }}>{t("home.events_hint", { kv: nf(norms.v_dip, lang) })}</div>
        </div>
      )}
      {card && <EventCard item={card} onClose={() => setCard(null)} onOpenCase={() => app.setTab("events")} />}

      {hasUnits && d.reports.length > 0 && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <b style={{ fontSize: 14 }}>{t("home.reports")}</b>
            {link(t("home.all"), () => app.setTab("reports"))}
          </div>
          {d.reports.slice(0, 3).map((r, i) => (
            <div key={"r" + r.id} onClick={() => openReport(r)} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: i ? "1px solid var(--border)" : undefined, cursor: "pointer", fontSize: 13 }}>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📄 G{r.unit} · {fmtDT(r.from_ts)} → {fmtT(r.to_ts)}</span>
              <span style={{ color: "var(--hint)", fontSize: 11, whiteSpace: "nowrap" }}>PDF · {r.pages} {t("report.pages")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
